import type { ReactNode } from "react";

/**
 * Markdown-lite: a small, safe subset, rendered as React elements.
 *
 * There is no `dangerouslySetInnerHTML` anywhere in this file, and no HTML parser. Every output
 * is a React element built from matched text, so raw HTML in model output is displayed as the
 * characters the model wrote rather than interpreted — which is the whole point. A renderer that
 * needed sanitising would be a renderer that could be got wrong.
 *
 * Supported, deliberately and no more:
 *   paragraphs · headings (#, ##, ###) · bullet and numbered lists · **bold** · *italics*
 *   `inline code` · ```fenced code``` · [links](url) with a safe scheme · citations
 *
 * Not supported: tables, images, blockquotes, footnotes, HTML. Anything unsupported falls through
 * as plain text, which is legible and harmless.
 *
 * Citations are the one Survey-specific extension: a `ragflow://…` or `[[fact:<id>]]` token is
 * turned into a `<Citation>` the page can render as a link. Nothing else is given special meaning.
 */

const SAFE_SCHEME = /^(https?:\/\/|\/|mailto:)/i;
const RAGFLOW = /^ragflow:\/\/[^\s)]+$/i;

export interface MarkdownLiteProps {
  text: string | null | undefined;
  /** Renders a citation token. Return null to show the raw token instead. */
  renderCitation?: (reference: string) => ReactNode;
}

interface Block {
  kind: "p" | "h1" | "h2" | "h3" | "ul" | "ol" | "code";
  lines: string[];
  language?: string;
}

/** Split source text into blocks. Fenced code is taken verbatim, including any markdown inside. */
function parseBlocks(text: string): Block[] {
  const blocks: Block[] = [];
  const lines = text.replace(/\r\n/g, "\n").split("\n");
  let index = 0;

  while (index < lines.length) {
    const line = lines[index];

    if (line.startsWith("```")) {
      const language = line.slice(3).trim() || undefined;
      const body: string[] = [];
      index += 1;
      while (index < lines.length && !lines[index].startsWith("```")) {
        body.push(lines[index]);
        index += 1;
      }
      index += 1; // closing fence, or end of input
      blocks.push({ kind: "code", lines: body, language });
      continue;
    }

    if (!line.trim()) {
      index += 1;
      continue;
    }

    const heading = /^(#{1,3})\s+(.*)$/.exec(line);
    if (heading) {
      blocks.push({
        kind: heading[1].length === 1 ? "h1" : heading[1].length === 2 ? "h2" : "h3",
        lines: [heading[2]],
      });
      index += 1;
      continue;
    }

    if (/^\s*[-*]\s+/.test(line)) {
      const items: string[] = [];
      while (index < lines.length && /^\s*[-*]\s+/.test(lines[index])) {
        items.push(lines[index].replace(/^\s*[-*]\s+/, ""));
        index += 1;
      }
      blocks.push({ kind: "ul", lines: items });
      continue;
    }

    if (/^\s*\d+[.)]\s+/.test(line)) {
      const items: string[] = [];
      while (index < lines.length && /^\s*\d+[.)]\s+/.test(lines[index])) {
        items.push(lines[index].replace(/^\s*\d+[.)]\s+/, ""));
        index += 1;
      }
      blocks.push({ kind: "ol", lines: items });
      continue;
    }

    const paragraph: string[] = [];
    while (
      index < lines.length &&
      lines[index].trim() &&
      !lines[index].startsWith("```") &&
      !/^(#{1,3})\s+/.test(lines[index]) &&
      !/^\s*[-*]\s+/.test(lines[index]) &&
      !/^\s*\d+[.)]\s+/.test(lines[index])
    ) {
      paragraph.push(lines[index]);
      index += 1;
    }
    blocks.push({ kind: "p", lines: paragraph });
  }

  return blocks;
}

/**
 * Inline formatting, one pass, longest-first so `**bold**` is not read as two italics.
 * Everything not matched stays literal text.
 */
function renderInline(
  source: string,
  renderCitation?: (reference: string) => ReactNode,
): ReactNode[] {
  const pattern =
    /(`[^`]+`)|(\*\*[^*]+\*\*)|(\*[^*]+\*)|(\[\[fact:[^\]]+\]\])|(\[[^\]]+\]\([^)\s]+\))|(ragflow:\/\/[^\s),]+)/g;
  const out: ReactNode[] = [];
  let cursor = 0;
  let key = 0;
  let match: RegExpExecArray | null;

  while ((match = pattern.exec(source)) !== null) {
    if (match.index > cursor) out.push(source.slice(cursor, match.index));
    const token = match[0];

    if (token.startsWith("`")) {
      out.push(<code key={key++}>{token.slice(1, -1)}</code>);
    } else if (token.startsWith("**")) {
      out.push(<strong key={key++}>{token.slice(2, -2)}</strong>);
    } else if (token.startsWith("[[fact:")) {
      const reference = token.slice(7, -2);
      out.push(
        <span key={key++}>{renderCitation?.(reference) ?? token}</span>,
      );
    } else if (token.startsWith("ragflow://")) {
      out.push(
        <span key={key++}>{renderCitation?.(token) ?? <code>{token}</code>}</span>,
      );
    } else if (token.startsWith("[")) {
      const link = /^\[([^\]]+)\]\(([^)\s]+)\)$/.exec(token);
      if (link && RAGFLOW.test(link[2])) {
        out.push(
          <span key={key++}>{renderCitation?.(link[2]) ?? <code>{link[1]}</code>}</span>,
        );
      } else if (link && SAFE_SCHEME.test(link[2])) {
        // Only http(s), site-relative and mailto links become anchors. `javascript:` and
        // `data:` are shown as text, because a link the reader cannot see the target of is
        // not a link worth having.
        out.push(
          <a key={key++} href={link[2]} target="_blank" rel="noreferrer noopener">
            {link[1]}
          </a>,
        );
      } else {
        out.push(token);
      }
    } else if (token.startsWith("*")) {
      out.push(<em key={key++}>{token.slice(1, -1)}</em>);
    } else {
      out.push(token);
    }
    cursor = match.index + token.length;
  }

  if (cursor < source.length) out.push(source.slice(cursor));
  return out;
}

export function MarkdownLite({ text, renderCitation }: MarkdownLiteProps) {
  if (!text) return null;
  const blocks = parseBlocks(text);

  return (
    <div className="md">
      {blocks.map((block, index) => {
        const key = `${block.kind}-${index}`;
        switch (block.kind) {
          case "code":
            return (
              <pre key={key} className="md__code">
                <code>{block.lines.join("\n")}</code>
              </pre>
            );
          case "h1":
            return <h3 key={key}>{renderInline(block.lines[0], renderCitation)}</h3>;
          case "h2":
            return <h4 key={key}>{renderInline(block.lines[0], renderCitation)}</h4>;
          case "h3":
            return <h5 key={key}>{renderInline(block.lines[0], renderCitation)}</h5>;
          case "ul":
            return (
              <ul key={key}>
                {block.lines.map((item, i) => (
                  <li key={i}>{renderInline(item, renderCitation)}</li>
                ))}
              </ul>
            );
          case "ol":
            return (
              <ol key={key}>
                {block.lines.map((item, i) => (
                  <li key={i}>{renderInline(item, renderCitation)}</li>
                ))}
              </ol>
            );
          default:
            return <p key={key}>{renderInline(block.lines.join(" "), renderCitation)}</p>;
        }
      })}
    </div>
  );
}
